import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useI18n } from '../i18n';
import { PageHeader } from '../components/layout/AppShell';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { StatusChip } from '../components/ui/StatusChip';
import { SkeletonGrid } from '../components/ui/Skeleton';
import { EmptyState } from '../components/ui/EmptyState';
import { TextField, TextAreaField } from '../components/ui/Field';
import { IconWave, IconDownload, IconStore, IconArrowRight, IconClose, IconInbox } from '../components/ui/icons';
import { assets, vocals, submissions, API_BASE } from '../lib/api';
import { apiErrorMessage } from '../lib/errors';
import { jobStateToChip } from '../lib/tokens';
import { useToast } from '../components/ui/Toast';
import { useJobPoll } from '../hooks/useJobPoll';
import type { AssetSummary, VocalProfile } from '../lib/types';

const PROCESSABLE = new Set<AssetSummary['kind']>(['audio', 'sample_pack']);
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function hz(fmt: (n: string) => string, value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '—';
  return fmt(String(Math.round(value)));
}

/** Vocal range profile summary (§ F-031 GET /v1/me/vocal-profile). */
function ProfileCard({ profile }: { profile: VocalProfile | null }) {
  const { t } = useI18n();
  const takeCount = profile?.take_count ?? 0;

  if (takeCount === 0) {
    return (
      <Card className="mb-8 p-5">
        <h2 className="type-heading-2 text-primary">{t.vocal.profileTitle}</h2>
        <p className="type-body-sm text-secondary mt-2">{t.vocal.profileEmpty}</p>
      </Card>
    );
  }

  const range = `${hz(t.vocal.hz, profile?.overall_lowest_hz)} – ${hz(t.vocal.hz, profile?.overall_highest_hz)}`;
  const stat = (label: string, value: string) => (
    <div className="flex flex-col gap-1">
      <span className="type-caption text-muted uppercase">{label}</span>
      <span className="type-heading-2 text-primary tnum">{value}</span>
    </div>
  );

  return (
    <Card className="mb-8 p-5">
      <div className="flex items-center justify-between">
        <h2 className="type-heading-2 text-primary">{t.vocal.profileTitle}</h2>
        <span className="type-caption text-muted">{t.vocal.takesLabel(takeCount)}</span>
      </div>
      <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3">
        {stat(t.vocal.rangeLabel, range)}
        {stat(t.vocal.meanLabel, hz(t.vocal.hz, profile?.overall_mean_hz))}
        {stat(t.vocal.keyLabel, profile?.latest_recommended_key ?? '—')}
      </div>
    </Card>
  );
}

/** Compose + send a submission of an acapella to a producer's inbox (F-046). */
function SubmitModal({ asset, onClose, onSent }: { asset: AssetSummary; onClose: () => void; onSent: () => void }) {
  const { t } = useI18n();
  const toast = useToast();
  const [recipient, setRecipient] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);

  const submit = async () => {
    const to = recipient.trim();
    if (!UUID_RE.test(to)) {
      setError(t.errors.fieldRequired);
      return;
    }
    setSending(true);
    setError(null);
    try {
      await submissions.create({ asset_id: asset.id, to_user_id: to, message: message.trim() || undefined });
      toast.push(t.submissions.sent, 'success');
      onSent();
      onClose();
    } catch (err) {
      setError(apiErrorMessage(err, t));
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 p-0 sm:items-center sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-label={t.submissions.composeTitle}
      onClick={onClose}
    >
      <Card
        className="w-full max-w-md p-5 sm:rounded"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <h2 className="type-heading-1 text-primary">{t.submissions.composeTitle}</h2>
            <p className="type-body-sm text-secondary mt-1">{t.submissions.composeBody}</p>
          </div>
          <button type="button" onClick={onClose} aria-label={t.common.close} className="focus-ring text-muted hover:text-primary">
            <IconClose size={20} />
          </button>
        </div>
        <p className="type-data text-secondary mb-4 truncate" title={asset.title}>
          {asset.title}
        </p>
        <div className="flex flex-col gap-4">
          <TextField
            label={t.submissions.recipientLabel}
            help={t.submissions.recipientHelp}
            value={recipient}
            onChange={(e) => setRecipient(e.target.value)}
            autoComplete="off"
            required
            error={error}
          />
          <TextAreaField
            label={t.submissions.messageLabel}
            placeholder={t.submissions.messagePlaceholder}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={3}
          />
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="md" onClick={onClose}>
              {t.common.cancel}
            </Button>
            <Button size="md" loading={sending} onClick={submit} iconRight={<IconArrowRight size={16} />}>
              {sending ? t.submissions.sending : t.submissions.send}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}

export function VocalWorkspace() {
  const { t, fmt } = useI18n();
  const toast = useToast();
  const [profile, setProfile] = useState<VocalProfile | null>(null);
  const [items, setItems] = useState<AssetSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeJobId, setActiveJobId] = useState<string | null>(null);
  const [starting, setStarting] = useState<string | null>(null);
  const [submitFor, setSubmitFor] = useState<AssetSummary | null>(null);

  const { job } = useJobPoll(activeJobId);

  const load = useCallback(async () => {
    try {
      const [prof, page] = await Promise.all([vocals.profile(), assets.mine({ limit: 50 })]);
      setProfile(prof);
      setItems(page.items);
      setError(null);
    } catch (err) {
      setError(apiErrorMessage(err, t));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  // When a vocal_process job settles, refresh the profile + acapella list.
  useEffect(() => {
    if (!job) return;
    if (job.state === 'complete') {
      toast.push(t.vocal.jobDone, 'success');
      setActiveJobId(null);
      void load();
    } else if (job.state === 'failed' || job.state === 'dlq') {
      toast.push(t.vocal.jobFailed, 'error');
      setActiveJobId(null);
    }
  }, [job, load, t, toast]);

  const process = async (assetId: string) => {
    setStarting(assetId);
    try {
      const res = await vocals.process(assetId);
      setActiveJobId(res.job_id);
      toast.push(t.vocal.jobQueued, 'info');
    } catch (err) {
      toast.push(apiErrorMessage(err, t), 'error');
    } finally {
      setStarting(null);
    }
  };

  const sources = items.filter((a) => PROCESSABLE.has(a.kind) && a.processing_state === 'ready');
  const acapellas = items.filter((a) => a.kind === 'acapella_pack' && a.processing_state === 'ready');
  const jobActive = activeJobId !== null && job?.state !== 'complete';

  if (loading) {
    return (
      <div>
        <PageHeader title={t.vocal.title} subtitle={t.vocal.subtitle} />
        <SkeletonGrid count={4} />
      </div>
    );
  }

  return (
    <div>
      <PageHeader title={t.vocal.title} subtitle={t.vocal.subtitle} />

      {error ? (
        <p className="type-body-sm text-danger mb-4" role="alert">
          {error}
        </p>
      ) : null}

      <ProfileCard profile={profile} />

      {jobActive ? (
        <Card className="mb-8 flex items-center gap-3 p-4">
          <StatusChip tone={jobStateToChip(job?.state ?? 'pending')} />
          <span className="type-body-sm text-secondary">
            {job?.state === 'running' || job?.state === 'awaiting_callback' ? t.vocal.jobRunning : t.vocal.jobQueued}
          </span>
        </Card>
      ) : null}

      {/* Sources ready to process */}
      <h2 className="type-heading-2 text-primary mb-3">{t.vocal.sourcesTitle}</h2>
      {sources.length === 0 ? (
        <EmptyState
          icon={<IconWave size={48} />}
          title={t.vocal.sourcesTitle}
          body={t.vocal.sourcesEmpty}
          action={
            <Link to="/studio">
              <Button size="lg">{t.vocal.goUpload}</Button>
            </Link>
          }
        />
      ) : (
        <div className="grid gap-4 grid-cols-[repeat(auto-fill,minmax(240px,1fr))]">
          {sources.map((asset) => (
            <Card key={asset.id} className="flex flex-col gap-3 p-3">
              <h3 className="type-heading-2 text-primary truncate" title={asset.title}>
                {asset.title}
              </h3>
              <p className="type-data text-secondary tnum">
                {fmt.duration(asset.duration_ms)} · {asset.kind.replace('_', ' ').toUpperCase()}
              </p>
              <Button
                variant="primary"
                size="sm"
                block
                iconLeft={<IconWave size={16} />}
                loading={starting === asset.id}
                disabled={jobActive}
                onClick={() => void process(asset.id)}
              >
                {starting === asset.id ? t.vocal.processingCta : t.vocal.processCta}
              </Button>
            </Card>
          ))}
        </div>
      )}

      {/* Resulting acapellas */}
      <h2 className="type-heading-2 text-primary mb-3 mt-10">{t.vocal.packsTitle}</h2>
      {acapellas.length === 0 ? (
        <p className="type-body-sm text-muted flex items-center gap-2">
          <IconInbox size={18} /> {t.vocal.packsEmpty}
        </p>
      ) : (
        <div className="grid gap-4 grid-cols-[repeat(auto-fill,minmax(240px,1fr))]">
          {acapellas.map((asset) => (
            <Card key={asset.id} className="flex flex-col gap-3 p-3">
              <h3 className="type-heading-2 text-primary truncate" title={asset.title}>
                {asset.title}
              </h3>
              <p className="type-data text-secondary tnum">
                {fmt.duration(asset.duration_ms)} · {(asset.key_camelot ?? '—')} · ACAPELLA
              </p>
              <div className="mt-1 flex flex-wrap gap-2">
                <a
                  href={`${API_BASE}/v1/me/licenses/${asset.id}/download?file=pack`}
                  target="_blank"
                  rel="noreferrer"
                  className="grow"
                >
                  <Button variant="secondary" size="sm" block iconLeft={<IconDownload size={16} />}>
                    {t.library.downloadPack}
                  </Button>
                </a>
                <Link to={`/listings/new?asset=${asset.id}`} className="grow">
                  <Button variant="ghost" size="sm" block iconLeft={<IconStore size={16} />}>
                    {t.vocal.publishCta}
                  </Button>
                </Link>
                <Button
                  variant="primary"
                  size="sm"
                  block
                  iconLeft={<IconArrowRight size={16} />}
                  onClick={() => setSubmitFor(asset)}
                >
                  {t.vocal.submitCta}
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {submitFor ? (
        <SubmitModal asset={submitFor} onClose={() => setSubmitFor(null)} onSent={() => setSubmitFor(null)} />
      ) : null}
    </div>
  );
}
